import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/context";

import { whatsappMetaConfigQueryKeys } from "../whatsapp-meta-config/whatsapp-meta-config.query-keys";
import { whatsappMetaConfigApi } from "./whatsapp-meta-config-detail.api";
import type { IUseWhatsappMetaConfigDetail } from "./whatsapp-meta-config-detail.hooks.interface";

const TEMPLATES_PAGE_SIZE = 25;

function readOrganizationIdFromCookie(): string | undefined {
  try {
    const raw = Cookies.get("authUser");
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as { organization_root_id?: string };
    const id = parsed.organization_root_id?.trim();
    return id || undefined;
  } catch {
    return undefined;
  }
}

export const useWhatsappMetaConfigDetail = (): IUseWhatsappMetaConfigDetail => {
  const { configId, clientId } = useParams<{ configId: string; clientId: string }>();
  const navigate = useNavigate();
  const { auth } = useAuth();

  const organizationId = React.useMemo(() => {
    // Quando acessado via /clients/:clientId/whatsapp-meta/:configId, usa o clientId como escopo
    if (clientId?.trim()) {
      return clientId.trim();
    }
    const fromAuth = auth.organization_root_id?.trim();
    if (fromAuth) {
      return fromAuth;
    }
    return readOrganizationIdFromCookie();
  }, [clientId, auth.organization_root_id, auth.token]);

  const backToList = React.useCallback(() => {
    if (clientId?.trim()) {
      navigate("/clients", { state: { openClientId: clientId.trim() } });
    } else {
      navigate("/whatsapp-meta");
    }
  }, [navigate, clientId]);

  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigsError,
    error: configsQueryError,
  } = useQuery({
    queryKey: [...whatsappMetaConfigQueryKeys.configs, organizationId ?? ""],
    queryFn: () => whatsappMetaConfigApi.list(),
    enabled: Boolean(organizationId && configId),
    select: (list) => list.find((c) => c.id === configId) ?? null,
  });

  const configsErrorMessage = React.useMemo(() => {
    if (!configsQueryError) {
      return null;
    }
    return configsQueryError instanceof Error
      ? configsQueryError.message
      : String(configsQueryError);
  }, [configsQueryError]);

  const {
    data: phoneNumbersData,
    isLoading: isPhoneNumbersLoading,
  } = useQuery({
    queryKey: [...whatsappMetaConfigQueryKeys.phoneNumbers(configId ?? ""), organizationId ?? ""],
    queryFn: () =>
      whatsappMetaConfigApi.phoneNumbers(configId as string, organizationId),
    enabled: Boolean(organizationId && configId && config),
  });

  const {
    data: templatesInfinite,
    isLoading: isTemplatesLoading,
    isFetchingNextPage: isFetchingTemplatesNextPage,
    hasNextPage: hasNextTemplatesPage,
    fetchNextPage: fetchNextTemplatesPage,
  } = useInfiniteQuery({
    queryKey: [...whatsappMetaConfigQueryKeys.templates(configId ?? ""), organizationId ?? ""],
    queryFn: ({ pageParam }) =>
      whatsappMetaConfigApi.messageTemplates(configId as string, {
        after: pageParam as string | undefined,
        limit: TEMPLATES_PAGE_SIZE,
        organizationScopeId: organizationId,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const list = lastPage.data ?? [];
      const after = lastPage.paging?.cursors?.after;
      const hasNext = Boolean(lastPage.paging?.next);
      if (!hasNext || !after || list.length === 0) {
        return undefined;
      }
      return after;
    },
    enabled: Boolean(organizationId && configId && config),
  });

  const templatesPages = templatesInfinite?.pages;

  return {
    configId,
    organizationId,
    config,
    isConfigLoading,
    isConfigsError,
    configsErrorMessage,
    backToList,
    phoneNumbersData,
    isPhoneNumbersLoading,
    templatesPages,
    isTemplatesLoading,
    isFetchingTemplatesNextPage,
    hasNextTemplatesPage: Boolean(hasNextTemplatesPage),
    fetchNextTemplatesPage,
  };
};
